/**
 * Friendships Vibe — Coaching Page
 *
 * State-based navigation version (setCurrentView instead of React Router).
 * Adapted from Deeper Dialogues with Vibe youth safety layer:
 *  - 90-second cooldown countdown
 *  - Always-visible youth crisis footer
 *  - Age-appropriate suggested prompts (no rehearsal)
 *  - Shorter, warmer UI language
 *
 * Australian English throughout.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Settings as SettingsIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useGuide } from '../context/GuideContext';
import coachingService from '../services/CoachingService';
import { hubCharacterId } from '../services/GuideSupportService';
import {
  CrisisResourcesCard,
  ProfessionalSupportCard,
  SubtleProfessionalHint,
  CoachingDisclaimer,
  UsageIndicator,
  ConsentGate,
  YouthCrisisFooter,
  CooldownCountdown
} from '../components/coaching/CoachingPanels';
import { renderMarkdown } from '../components/coaching/renderMarkdown.jsx';

// The coach here is whichever of the six guides the user chose at onboarding -
// the same voice the floating guide button opens. Until 2026-09-09 this page
// hard-coded "Vaulty" (the vault app's butler, a clone leftover) and never
// told the Hub which guide to speak as, so the header said one name and the
// replies came in Sage's voice whatever the user had picked.

const suggestedPrompts = [
  "I'm having a hard time with a friend right now. Can we talk it through?",
  "I feel like I don't really fit in anywhere.",
  "How do I set boundaries with someone without making it weird?",
  "I keep overthinking what people think of me."
];

export default function CoachingPage() {
  const { setCurrentView } = useApp();
  const { selectedGuide: guide } = useGuide();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [crisis, setCrisis] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [revisionContext, setRevisionContext] = useState(null);
  const [cooldownEnd, setCooldownEnd] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    // Check for module revision context from "Revise with Coach" CTA
    try {
      const stored = sessionStorage.getItem('revisionContext');
      if (stored) {
        setRevisionContext(JSON.parse(stored));
        sessionStorage.removeItem('revisionContext');
      }
    } catch { /* ignore parse errors */ }

    (async () => {
      const res = await coachingService.getProfile();
      if (res?.success) setProfile(res.profile);
      setLoadingProfile(false);
    })();

    return () => {
      coachingService.endSession().catch(() => {});
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, crisis, professional]);

  const handleGrantConsent = async (opts) => {
    const res = await coachingService.grantConsent(opts);
    if (res?.success) setProfile(res.profile);
    return res;
  };

  const ensureSession = async () => {
    if (sessionStarted) return;
    const sessionOpts = revisionContext
      ? { sessionType: 'reflection', topic: `Revising: ${revisionContext.moduleName || revisionContext.moduleId}` }
      : { sessionType: 'exploration' };
    await coachingService.startSession(sessionOpts);
    setSessionStarted(true);
  };

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    // Check client-side cooldown
    const remaining = coachingService.getCooldownRemaining();
    if (remaining > 0) {
      setCooldownEnd(Date.now() + remaining);
      return;
    }

    setSending(true);
    setError(null);
    setCooldownEnd(null);

    await ensureSession();

    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setInput('');

    const res = await coachingService.askCoach(trimmed, {
      characterId: hubCharacterId(guide.id),
      userContext: revisionContext ? {
        currentModule: revisionContext.moduleId,
        moduleName: revisionContext.moduleName,
        sectionId: revisionContext.sectionId,
        sectionName: revisionContext.sectionName
      } : {}
    });

    if (res?.success) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.response,
        character: res.character
      }]);
      setCrisis(res.crisisResources || null);
      setProfessional(res.professionalSupport || null);
      setUsage(res.usage || null);
      // Start cooldown after successful message
      setCooldownEnd(Date.now() + 90_000);
    } else if (res?.error === 'cooldown') {
      setCooldownEnd(Date.now() + (res.cooldownRemaining || 90_000));
    } else {
      setError(res?.response || 'Something went wrong.');
      if (res?.dailyCapExceeded) {
        setUsage({
          tier: res.tier,
          dailyLimit: res.dailyLimit,
          used: res.dailyLimit,
          remaining: 0,
          upgradeUrl: res.upgradeUrl
        });
      }
      setMessages(prev => [...prev, { role: 'assistant', content: res?.response || '...', isError: true }]);
    }
    setSending(false);
  };

  if (loadingProfile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-slate-500">Loading…</p>
      </div>
    );
  }

  if (!profile?.consent?.granted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-10 px-4 sm:px-6">
        <ConsentGate
          coachName={guide.name}
          onAccept={handleGrantConsent}
          onDecline={() => setCurrentView('home')}
        />
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-48">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: guide.accentColor }}
              aria-hidden="true"
            >
              {guide.name[0]}
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-slate-900">Chat with {guide.name}</h1>
              <p className="text-xs sm:text-sm text-slate-600">
                AI coach — not a real person. Here to help you think things through.
              </p>
            </div>
          </div>
          <button
            onClick={() => setCurrentView('coaching-privacy')}
            className="p-2 rounded-lg hover:bg-white text-slate-500"
            title="Your coaching data"
          >
            <SettingsIcon size={18} />
          </button>
        </header>

        <div className="mb-3 space-y-2">
          <CoachingDisclaimer />
          <UsageIndicator usage={usage} />
          <CooldownCountdown cooldownEnd={cooldownEnd} />
        </div>

        {!hasMessages && (
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
            {revisionContext ? (
              <>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  Ready to revise <strong>{revisionContext.moduleName || revisionContext.moduleId}</strong>
                  {revisionContext.sectionName ? ` — ${revisionContext.sectionName}` : ''}? Let's make sure it sticks.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => send(`I just finished the section on ${revisionContext.sectionName || revisionContext.moduleId} in ${revisionContext.moduleName || 'the module'}. Can you help me check I've understood it and apply it to my situation?`)}
                    className="w-full text-left text-sm sm:text-base text-slate-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-3 transition"
                  >
                    Help me revise and apply what I just learned
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  Hey {profile?.personalContext?.firstName || 'there'} — what's going on? We can
                  talk through friendship stuff, figure out boundaries, or just unpack something
                  that's been on your mind.
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {suggestedPrompts.map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="text-left text-sm sm:text-base text-slate-700 bg-slate-50 hover:bg-blue-50 hover:text-blue-800 border border-slate-200 rounded-lg p-3 transition"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="space-y-4">
          <AnimatePresence>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm sm:text-base leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : m.isError
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-white text-slate-800 shadow-sm'
                  }`}
                >
                  {m.role === 'assistant' && !m.isError ? renderMarkdown(m.content) : m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {crisis && <CrisisResourcesCard resources={crisis} />}
          {!crisis && professional?.tone === 'amber' && <ProfessionalSupportCard support={professional} />}
          {!crisis && professional?.tone === 'subtle' && <SubtleProfessionalHint support={professional} />}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl px-4 py-3 shadow-sm text-slate-500 text-sm">
                {guide.name} is thinking…
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Fixed bottom: crisis footer + input form */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2">
          <YouthCrisisFooter />
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="px-4 sm:px-6 py-3 border-t border-slate-100"
        >
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              placeholder="What's on your mind?"
              className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-h-40"
              disabled={sending}
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="flex-shrink-0 p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
            >
              <Send size={18} />
            </button>
          </div>
          {error && (
            <p className="max-w-3xl mx-auto text-xs sm:text-sm text-red-600 mt-1">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
}
